import { jsxs, jsx } from 'react/jsx-runtime';
import React__default, { useEffect, useMemo, Fragment } from 'react';
import { useEditorState } from '@tiptap/react';
import { createGlobalStyle, styled } from 'styled-components';
import { ToolbarSelectorComponent, ToolbarButtonComponent, ToolbarDropdownComponent, ToolbarToggleComponent } from './ToolbarItems.js';
import { ToolbarItemType } from './types.js';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';

const ToolbarGlobalStyles = createGlobalStyle([
  ``,
  `{:root{--toolbar-height:`,
  `px;}}`
], mediaMobile, (p) => p.toolbarHeight || 0);
const Content = styled.div.withConfig({
  displayName: "Toolbar__Content",
  componentId: "sc-c0e55256-0"
})([
  `display:flex;flex-grow:1;overflow-x:visible;align-items:center;padding:4px;`,
  `{padding:0.45rem 0.5rem;}flex-wrap:nowrap;width:100%;min-width:100%;max-width:100%;flex-shrink:0;[role='group']{display:flex;flex-wrap:nowrap;align-items:center;gap:4px;}`
], mediaMobile);
const ToolbarRoot = styled.div.withConfig({
  displayName: "Toolbar__ToolbarRoot",
  componentId: "sc-c0e55256-1"
})([
  `display:flex;width:100%;min-width:100%;max-width:100%;flex-wrap:nowrap;cursor:default;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;z-index:150;display:flex;align-items:center;background:var(--fabric-color-bg-primary);backdrop-filter:blur(8px);pointer-events:auto;border-top:1px solid var(--fabric-color-border-primary);border-bottom:1px solid var(--fabric-color-border-primary);[role='group']{display:flex;align-items:center;gap:6px;&:empty{display:none;}}&&{user-select:none;}&[data-floating]{border:1px solid var(--fabric-color-border-primary);box-shadow:0 2px 8px 1px rgba(128,128,128,0.187);background:var(--fabric-color-bg-primary);backdrop-filter:blur(8px);border-radius:12px;flex-wrap:nowrap;width:fit-content;max-width:none;> [role='group']{display:flex;flex-wrap:nowrap;align-items:center;gap:6px;}}`
]);
const ToolbarSeparator = styled.div.withConfig({
  displayName: "Toolbar__ToolbarSeparator",
  componentId: "sc-c0e55256-2"
})([
  `width:1px;flex-shrink:0;background-color:var(--fabric-color-border-primary);margin:0 6px;padding:10px 0;`
]);
const preventEditorLoseFocus = (editor) => (e) => {
  if (editor.isFocused) {
    e.preventDefault();
  } else {
    editor.chain().focus();
  }
};
const Toolbar = ({ editor, groups, floating, onToolbarHeightChange, noSeperator = false, mobileTarget, suffix }) => {
  const toolbarRef = React__default.useRef(null);
  const [toolbarRect] = useDomContentRect(toolbarRef.current);
  useEffect(() => {
    if (!toolbarRef.current) {
      return;
    }
    const handleTouchMove = (e) => {
      if (e.target instanceof HTMLElement && e.target.closest("[data-prevent-touch-move]")) {
        e.preventDefault();
      }
    };
    toolbarRef.current.addEventListener("touchmove", handleTouchMove, {
      passive: false
    });
    return () => {
      toolbarRef.current?.removeEventListener("touchmove", handleTouchMove);
    };
  }, [
    editor
  ]);
  React__default.useEffect(() => {
    onToolbarHeightChange?.(toolbarRect?.height || 0);
  }, [
    toolbarRect?.height,
    onToolbarHeightChange
  ]);
  const { selection } = useEditorState({
    editor,
    selector: (snapshot) => ({
      selection: snapshot.editor.state.selection
    })
  });
  const mappedGroups = useMemo(() => groups.filter(({ isHidden }) => !isHidden?.(selection)).map(({ name, label, items }, index, arr) => {
    return /* @__PURE__ */ jsxs(Fragment, {
      children: [
        /* @__PURE__ */ jsx("div", {
          "aria-label": typeof label === "function" ? label(editor) : label,
          role: "group",
          children: editor && renderToolbarGroupItems(editor, items, mobileTarget)
        }, name),
        index < arr.length - 1 && !noSeperator && /* @__PURE__ */ jsx(ToolbarSeparator, {
          role: "separator"
        }, `separator-${name}`)
      ]
    }, `fragment-${name}`);
  }), [
    groups,
    selection,
    editor,
    mobileTarget,
    noSeperator
  ]);
  const hash = useMemo(() => {
    return mappedGroups.map((group) => group.key).join("-");
  }, [
    mappedGroups
  ]);
  if (mappedGroups.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs(ToolbarRoot, {
    "data-floating": floating,
    className: "dashboard_scrollbar",
    role: "toolbar",
    draggable: false,
    // onTouchStart={preventEditorLoseFocus(editor)}
    // onTouchEnd={preventEditorLoseFocus(editor)}
    onClick: preventEditorLoseFocus(editor),
    onMouseDown: preventEditorLoseFocus(editor),
    "data-tiptap-toolbar": true,
    "data-tiptap-fixed-toolbar": true,
    ref: toolbarRef,
    children: [
      /* @__PURE__ */ jsx(ScrollArea.Default, {
        orientation: "horizontal",
        scrollbarVariant: "none",
        asFlex: true,
        innerDisplayFlex: true,
        containOverscroll: true,
        style: {
          flexGrow: 1
        },
        children: /* @__PURE__ */ jsxs(Content, {
          children: [
            /* @__PURE__ */ jsx(ToolbarGlobalStyles, {
              toolbarHeight: toolbarRect?.height
            }),
            mappedGroups
          ]
        })
      }),
      suffix
    ]
  }, hash);
};
const renderToolbarGroupItems = (editor, items, mobileTarget) => {
  return items.map((item) => {
    switch (item.type) {
      case ToolbarItemType.Toggle:
        return /* @__PURE__ */ jsx(ToolbarToggleComponent, {
          editor,
          item
        }, item.key);
      case ToolbarItemType.Dropdown:
        return /* @__PURE__ */ jsx(ToolbarDropdownComponent, {
          editor,
          item
        }, item.key);
      case ToolbarItemType.Button:
        return /* @__PURE__ */ jsx(ToolbarButtonComponent, {
          editor,
          item
        }, item.key);
      case ToolbarItemType.Selector:
        return /* @__PURE__ */ jsx(ToolbarSelectorComponent, {
          editor,
          item,
          mobileTarget
        }, item.key);
      case ToolbarItemType.Custom:
        return item.render(item.key, editor, false);
    }
  });
};

export { Toolbar as default };
//# sourceMappingURL=Toolbar.js.map
